import { useEffect, useRef, useState, useTransition } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { ClipboardPaste } from 'lucide-react'
import { PlusIcon } from 'lucide-react'
import { useRxCollection } from 'rxdb-hooks'
import { FEATURE_TO_COINS } from 'shared/feature-to-coins'
import { Button } from 'ui/button'
import { Input } from 'ui/input'
import { cn } from 'ui/lib/utils'
import { toast } from 'ui/sonner'
import { v4 as uuidv4 } from 'uuid'

import { edgeClient } from '~/api/edge'
import { isSttSupported } from '~/boundedContext/list/components/audio-recorder/utils'
import { LinearSlideAnimation } from '~/components/linear-slide-animation'
import { RoundedBorderAnimation } from '~/components/rounded-border-animation'
import { TapEffect } from '~/components/tap-effect'
import { Card } from '~/db/types'
import { useIsOnline } from '~/hooks/use-is-online'
import { useAccountStore } from '~/stores/account'
import { useSettingsStore } from '~/stores/settings'

import { SuggestionsList } from '../suggestion-list'

import { INSERT_FORM_FIRST_INPUT_NAME, INSERT_FORM_SECOND_INPUT_NAME } from './const'
import { SpeechToTextButton } from './elements'
import { getFirstInputPlaceholder, getSecondInputPlaceholder } from './utils'

type InsertFormProps = {
  className?: string
  word?: string
}

export const InsertForm = ({ className, word }: InsertFormProps) => {
  const userId = useAccountStore(state => state.id)
  const coins = useAccountStore(state => state.coins)
  const sourceLanguage = useSettingsStore(state => state.sourceLanguage)
  const learnLanguage = useSettingsStore(state => state.learnLanguage)
  const enqueueCardIdRequiredAutofill = useSettingsStore(state => state.enqueueCardIdRequiredAutofill)

  const revalidateAccount = useAccountStore(state => state.revalidateAccount)
  const decreaseCoins = useAccountStore(state => state.decreaseCoins)
  const collection = useRxCollection<Card>('cards')
  const [translations, setTranslations] = useState<string[]>([])
  const [, startTransition] = useTransition()
  const setTranslationsWithoutBlocking = (value: string[]) => startTransition(() => setTranslations(value))
  const fetchedTranslationsRef = useRef<string[]>([])
  const [isTranslationsDisplayed, setIsTranslationsDisplayed] = useState(false)
  const [isSuggestionsLoading, setIsSuggestionsLoading] = useState(false)
  const [isVoiceLoading, setIsVoiceLoading] = useState(false)
  const { t } = useTranslation()

  const firstInputRef = useRef<HTMLInputElement | null>(null)
  const secondInputRef = useRef<HTMLInputElement | null>(null)
  const [detectedFirstInputLanguage, setDetectedFirstInputLanguage] = useState<string | undefined>()
  const isOnline = useIsOnline()

  const onSuggestionSelect = (translation: string) => {
    if (!secondInputRef.current) return

    secondInputRef.current.value = translation
    setTranslationsWithoutBlocking([])
    setIsTranslationsDisplayed(false)
    void onSubmit()
  }

  const cleanFields = () => {
    if (firstInputRef.current) firstInputRef.current.value = ''

    if (secondInputRef.current) secondInputRef.current.value = ''
  }

  const populateTranslations = async () => {
    if (!isOnline) return

    const termValue = firstInputRef.current?.value
    if (!termValue) return

    if (coins <= 0) return

    setIsSuggestionsLoading(true)
    try {
      const res = await edgeClient.translation
        .$get({
          query: {
            text: termValue,
            languageA: sourceLanguage.value,
            languageB: learnLanguage.value,
          },
        })
        .finally(() => setIsSuggestionsLoading(false))

      if (res.status === 403) void revalidateAccount()

      if (res.status !== 200) return

      const data = await res.json()

      decreaseCoins(FEATURE_TO_COINS.translation)
      setTranslationsWithoutBlocking(data.translations)
      // Cached initial array to be able to filter and reset back all suggestions
      fetchedTranslationsRef.current = data.translations
      if (data.from) setDetectedFirstInputLanguage(data.from)
    } catch (error) {
      toast.error(t('Network error'))
      console.error({ message: 'Network error', error })
      return
    }
  }

  const paste = async () => {
    try {
      const text = await navigator.clipboard.readText()
      if (!firstInputRef.current) return

      firstInputRef.current.value = text
      void populateTranslations()
      secondInputRef.current?.focus()
    } catch {
      // Clipboard access can be denied by the user or unsupported by the platform
      // (e.g. Mobile Safari without the required permission). Silently ignore so the
      // user can fall back to a manual paste gesture.
    }
  }

  const onSubmit = async () => {
    setIsSuggestionsLoading(false)
    const firstInputText = firstInputRef.current?.value
    const secondInputText = secondInputRef.current?.value
    const isFirstInputLearnLanguage = detectedFirstInputLanguage === learnLanguage.value
    setDetectedFirstInputLanguage(undefined)
    const sourceText = isFirstInputLearnLanguage ? secondInputText : firstInputText
    const learnText = isFirstInputLearnLanguage ? firstInputText : secondInputText
    if (!learnText || !sourceText || !learnLanguage || !sourceLanguage)
      return console.error({ message: 'Failed to submit card: Missing required fields' })

    const obj: Omit<Card, 'updatedAt'> = {
      id: uuidv4(),
      sourceText,
      learnText,
      learnLanguage: learnLanguage.value,
      sourceLanguage: sourceLanguage.value,
      createdAt: new Date().getTime(),
      userId,
      snoozeCountSource: 0,
      snoozeCountLearn: 0,
    }
    cleanFields()
    // it's necessary to prevent focus on the first input
    // after submitting a text during stt
    const isSecondInputFocused = document.activeElement === secondInputRef.current
    if (isSecondInputFocused) firstInputRef.current?.focus()

    const upsertResult = await collection?.upsert(obj)

    if (!upsertResult) return

    enqueueCardIdRequiredAutofill(obj.id)
  }

  const isNoCoins = coins <= 0
  useEffect(() => {
    const skipPopulateOnWordFromQuery = !word || isNoCoins
    if (skipPopulateOnWordFromQuery) return

    // oxlint-disable-next-line react/set-state-in-effect
    void populateTranslations()
    // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
  }, [word, isNoCoins])

  useEffect(() => {
    const secondInput = secondInputRef.current
    if (!secondInput) return
    const onSecondInputBlur = () => setIsTranslationsDisplayed(false)
    secondInput.addEventListener('blur', onSecondInputBlur, false)

    return () => {
      secondInput.removeEventListener('blur', onSecondInputBlur)
    }
  }, [])

  return (
    <div className={cn('flex h-fit items-end', className)}>
      <form
        className="flex w-full flex-col gap-2 px-1"
        onSubmit={e => {
          e.preventDefault()
          void onSubmit()
        }}
      >
        <Input
          ref={firstInputRef}
          aria-label={t('Input for either source or learn text')}
          className="grow"
          defaultValue={word}
          maxLength={100}
          name={INSERT_FORM_FIRST_INPUT_NAME}
          onBlur={populateTranslations}
          onFocus={() => {
            setTranslationsWithoutBlocking([])
            setDetectedFirstInputLanguage(undefined)
            fetchedTranslationsRef.current = []
            // warm up the edge server
            if (isOnline)
              edgeClient.warmup.$get().catch(error => {
                toast.error(t('Network error'))
                console.error({ message: 'Network error', error })
              })
          }}
          placeholder={getFirstInputPlaceholder(isOnline, t, sourceLanguage)}
          // TODO: implement using debounce
          required
        />
        <div className="relative">
          {isTranslationsDisplayed && !!translations.length && (
            <SuggestionsList onSuggestionSelect={onSuggestionSelect} translations={translations} />
          )}
          <Input
            ref={secondInputRef}
            aria-label={t('Translation of the text from the input above')}
            autoFocus={!!word}
            maxLength={100}
            name={INSERT_FORM_SECOND_INPUT_NAME}
            onChange={e =>
              setTranslationsWithoutBlocking(
                fetchedTranslationsRef.current.filter(translation =>
                  translation.toLowerCase().includes(e.currentTarget.value.toLowerCase()),
                ),
              )
            }
            onFocus={() => setIsTranslationsDisplayed(true)}
            placeholder={getSecondInputPlaceholder(isOnline, t, learnLanguage)}
            required
          />
          <LinearSlideAnimation className="mx-auto w-[98%]" isLoading={isSuggestionsLoading} />
        </div>

        <div className="flex items-center justify-center gap-2">
          <TapEffect>
            <Button
              aria-label={t('paste text into the field button')}
              onClick={paste}
              size="iconLg"
              type="button"
              variant="icon"
            >
              <ClipboardPaste />
            </Button>
          </TapEffect>
          <TapEffect>
            <Button aria-label={t('submit adding an item')} size="iconLg" type="submit" variant="icon">
              <PlusIcon />
            </Button>
          </TapEffect>
          <RoundedBorderAnimation
            className={clsx(sourceLanguage?.value && !isSttSupported(sourceLanguage.value) && 'hidden')}
            isLoading={isVoiceLoading}
          >
            <SpeechToTextButton
              onTextFetched={text => {
                if (firstInputRef.current) firstInputRef.current.value = text

                setTranslationsWithoutBlocking([])
                void populateTranslations()
                setIsTranslationsDisplayed(true)
              }}
              setIsLoading={setIsVoiceLoading}
            />
          </RoundedBorderAnimation>
        </div>
      </form>
    </div>
  )
}
